// The lobby's landscape preview (docs/platform/ClientShell.md "Lobby preview"): the world a match
// of the lobby's options will be played on, as the original's online lobby shows it
// (wmd_onlinelobby `MNU_Random_Landscape`, 418x207 in the 1920x1080 design space).
//
// - The world: `lobbyRules` derives the generator's rules from the lobby state as the server's
//   match builder does (crates/server/src/lobby_spec.rs; crates/server/tests/lobby_preview.rs
//   restates it and checks it against the built setups), so every member previews the same world.
//   The preview worker (sim/preview.worker.ts) generates it with `sim::LandPreview`, the match's
//   generator, and returns the previewer's picture (`sim::view::preview`); render/preview.ts paints
//   it at the surface's 1024x512.
// - When: the original regenerates whenever the landscape options change, as soon as its
//   generator is idle (`UICallback_OnlineLobby_NewRandomSeed_Monitor` 0xf033f0,
//   `UICallback_OnlineLobby_GraphicalOnIdle` 0xf03ec0; in a fort world also when the alliances'
//   first forts change). Here edits within `DEBOUNCE_MS` make one request, the worker abandons a
//   generation whose rules are no longer wanted, and pictures are kept by rules (`CACHE`): going
//   back to earlier options shows their picture at once.
// - What: `TEX_ThemeBacker` (the theme's `FrontendBackground`, `NDSPRIMER_Theme_Background`
//   0xec95c0), over it `TEX_ChannelThumbnail_01_*` (the surface, hidden while the generator is
//   busy: `UICallback_OnlineLobby_OnIdle` 0xf047c0) and, while it is busy, `MNU_LoadingRandomLandscape`
//   (activator `UICallback_Is_Landscape_Generator_Busy` 0xef5140): `XOM_RLGLoading`,
//   HU_RLGLoadingIcon_01's looping "Go" (128x128, camera z 300) centred 1 unit left of and below
//   the thumbnail's centre. The host's click on the thumbnail randomises the landscape within the
//   theme (`NDSInterface_Lobby::NDSPRIMER_Generate_Random` 0xeb3030; here `RandomiseLandscape`).
import type { Catalogue, LobbyState } from '../net/protocol.ts';
import { assetJson, assetUrl } from '../assets.ts';
import { decodePreview } from '../sim/view.ts';
import { PreviewPainter } from '../render/preview.ts';
import { UiXom } from '../render/uixom.ts';
import type { FromPreview, ToPreview } from '../sim/preview.worker.ts';

/** The widget's size (`TEX_ChannelThumbnail_01_Host`, design units = CSS pixels). */
export const PREVIEW_CSS_W = 418, PREVIEW_CSS_H = 207;
const DEBOUNCE_MS = 150;
/** Pictures kept (by rules). */
const CACHE = 12;
const LOADING_XDESC = 'DataLinux/Objects/UI/HU_RLGLoadingIcon_01.xDesc';

/** One fort alliance's lobby state (`matchsetup::LobbyFort`). */
export type FortRule = { player: number; fort: number; extra: number } | null;
/** The rules of a world (`sim::PreviewRules`). */
export type WorldRules = { theme: string; template: string; seed: number; size: number; buildings: boolean; forts: [FortRule, FortRule] };

/** The world the server's builder will make of the lobby (`LobbyData::build_in_world`): the
 * template of the landscape's type and variation (`Theme.types[land_type].templates[variation]`,
 * the builder's `ThemeMan::GetLandscapeTypeVariationPath` of the lobby options 4..6); in a fort
 * theme per fort alliance (colour 0, 1) the first occupied slot of that colour
 * (`GetFirstPlayerOfEachAlliance`: its index among the occupied slots, which are the match's teams)
 * with its fort and extras. Null when the catalogue lacks the theme, the type or the variation (the
 * server refuses such a lobby). */
export function lobbyRules(s: LobbyState, cat: Catalogue | null): WorldRules | null {
  const land = s.settings.landscape;
  const theme = cat?.themes.find(t => t.name === land.theme);
  const template = theme?.types[land.land_type]?.templates[land.variation];
  if (!theme || template === undefined) return null;
  const used = s.slots.filter(sl => sl.occupant.t !== 'Open');
  const fort = (alliance: number): FortRule => {
    const p = theme.fort ? used.findIndex(sl => sl.team.colour === alliance) : -1;
    return p < 0 ? null : { player: p, fort: used[p].team.fort ?? 0, extra: used[p].team.fort_extra ?? 0 };
  };
  return { theme: theme.name, template, seed: land.seed >>> 0, size: land.size, buildings: land.buildings, forts: [fort(0), fort(1)] };
}

/** `PreviewRules::encode` (crates/sim/src/api/preview.rs). */
export function encodeRules(r: WorldRules): Uint8Array {
  const te = new TextEncoder();
  const strs = [te.encode(r.theme), te.encode(r.template)];
  const out = new Uint8Array(4 + strs[0].length + strs[1].length + 9 + 2 * 13);
  const dv = new DataView(out.buffer);
  let o = 0;
  for (const s of strs) {
    dv.setUint16(o, s.length, true);
    out.set(s, o + 2);
    o += 2 + s.length;
  }
  dv.setUint32(o, r.seed, true);
  dv.setUint32(o + 4, r.size, true);
  out[o + 8] = r.buildings ? 1 : 0;
  o += 9;
  for (const f of r.forts) {
    out[o] = f ? 1 : 0;
    dv.setUint32(o + 1, f?.player ?? 0, true);
    dv.setUint32(o + 5, f?.fort ?? 0, true);
    dv.setUint32(o + 9, f?.extra ?? 0, true);
    o += 13;
  }
  return out;
}

/** A painted world: the surface as a bitmap and the theme's sky. */
type Picture = { key: string; rules: WorldRules; sky: string; bitmap: ImageBitmap };

/** Per shown picture: the rules, when they were asked for and how long each stage took (ms). */
export type PreviewTiming = {
  key: string; theme: string; template: string; seed: number;
  /** From the lobby state that asked for it to the picture on screen. */
  latencyMs: number;
  /** A cached picture (no generation). */
  cached: boolean;
  /** Generator frames and time in the worker (from the request's arrival), page loading, painting. */
  frames?: number; generateMs?: number; pagesMs?: number; drawMs?: number;
};

/** Test hooks (`window.__wmd.preview`). */
export type PreviewStats = {
  /** The rules wanted now (`PreviewRules::encode` bytes as hex) and the picture shown. */
  wanted: string | null; shown: string | null; busy: boolean;
  ready?: { fetchMs: number; wasmMs: number; bundleMs: number; generatorMs: number };
  timings: PreviewTiming[]; errors: string[];
};

let sprites: Promise<{ sprites: Record<string, { page: string; rect: number[] }>; pages: Record<string, { png?: string }> }> | null = null;
const skies = new Map<string, Promise<ImageBitmap | null>>();

/** A ui_textures sprite by file name (the theme sky), cut out of its atlas page. */
function uiSprite(name: string): Promise<ImageBitmap | null> {
  let p = skies.get(name);
  if (!p) {
    sprites ??= assetJson('ui/sprites.json');
    p = (async () => {
      const sp = await sprites;
      const s = sp?.sprites[`uidata/ui_textures/${name.toLowerCase()}`];
      const png = s ? sp.pages[s.page]?.png : null;
      if (!s || !png) return null;
      const blob = await (await fetch(assetUrl(png))).blob();
      return createImageBitmap(blob, s.rect[0], s.rect[1], s.rect[2], s.rect[3]);
    })().catch(e => { console.warn('preview sky', name, e); return null; });
    skies.set(name, p);
  }
  return p;
}

export class PreviewWidget {
  readonly el = document.createElement('div');
  readonly stats: PreviewStats = { wanted: null, shown: null, busy: false, timings: [], errors: [] };
  /** The host's click on the picture: a random landscape of the theme. */
  onRandomise: (() => void) | null = null;
  private canvas = document.createElement('canvas');
  private ctx: CanvasRenderingContext2D;
  private worker: Worker | null = null;
  private painter: PreviewPainter | null = null;
  private xom: UiXom | null = null;
  /** The rules wanted (key = encoded hex), since when, and the request sent for them. */
  private want: { key: string; rules: WorldRules; bytes: Uint8Array; at: number } | null = null;
  private requests = new Map<number, { key: string; rules: WorldRules; at: number }>();
  private nextId = 1;
  private debounce: number | undefined;
  private shown: Picture | null = null;
  private sky: ImageBitmap | null = null;
  private cache = new Map<string, Picture>();
  /** Pictures are painted one at a time (they share the painter's surface). */
  private painting: Promise<void> = Promise.resolve();
  private busySince = 0;
  private frame = 0;

  constructor() {
    this.el.className = 'lobby-preview';
    this.canvas.style.width = `${PREVIEW_CSS_W}px`;
    this.canvas.style.height = `${PREVIEW_CSS_H}px`;
    this.ctx = this.canvas.getContext('2d')!;
    this.el.appendChild(this.canvas);
    this.canvas.addEventListener('click', () => this.onRandomise?.());
  }

  /** The lobby changed: the world of its options becomes the wanted one. */
  update(s: LobbyState, cat: Catalogue | null, host: boolean): void {
    this.canvas.classList.toggle('clickable', host);
    this.canvas.title = host ? 'Random landscape of this theme' : '';
    const rules = lobbyRules(s, cat);
    if (!rules) return;
    const bytes = encodeRules(rules);
    const key = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    if (this.want?.key === key) return;
    this.want = { key, rules, bytes, at: performance.now() };
    this.stats.wanted = key;
    const hit = this.cache.get(key);
    if (hit) {
      this.cache.delete(key);
      this.cache.set(key, hit);
      this.show(hit, { key, theme: rules.theme, template: rules.template, seed: rules.seed, latencyMs: 0, cached: true });
      return;
    }
    this.setBusy(true);
    clearTimeout(this.debounce);
    this.debounce = window.setTimeout(() => {
      this.debounce = undefined;
      this.request();
    }, DEBOUNCE_MS);
  }

  /** Before a match: the worker and the GL context go (their memory is the match's); the next
   * `update` starts them again. */
  stop(): void {
    clearTimeout(this.debounce);
    this.debounce = undefined;
    this.worker?.terminate();
    this.worker = null;
    this.painter?.dispose();
    this.painter = null;
    this.requests.clear();
    this.want = null;
    this.setBusy(false);
  }

  /** The shown picture (the 1024x512 surface) as a PNG data URL, or null (tests). */
  pictureDataUrl(): string | null {
    if (!this.shown) return null;
    const c = document.createElement('canvas');
    c.width = this.shown.bitmap.width;
    c.height = this.shown.bitmap.height;
    c.getContext('2d')!.drawImage(this.shown.bitmap, 0, 0);
    return c.toDataURL('image/png');
  }

  private request(): void {
    const w = this.want;
    if (!w) return;
    const id = this.nextId++;
    this.requests.set(id, { key: w.key, rules: w.rules, at: w.at });
    this.post({ type: 'rules', id, rules: w.bytes });
  }

  private post(m: ToPreview): void {
    if (!this.worker) {
      const worker = new Worker(new URL('../sim/preview.worker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (e: MessageEvent<FromPreview>) => this.onWorker(e.data);
      worker.onerror = e => this.error(`preview worker: ${e.message}`);
      worker.postMessage({ type: 'init', wasmUrl: '/simwasm.wasm', bundleUrl: assetUrl('bundle.bin') } satisfies ToPreview);
      this.worker = worker;
    }
    this.worker.postMessage(m);
  }

  private onWorker(m: FromPreview): void {
    switch (m.type) {
      case 'ready':
        this.stats.ready = { fetchMs: m.fetchMs, wasmMs: m.wasmMs, bundleMs: m.bundleMs, generatorMs: m.generatorMs };
        break;
      case 'view': {
        const req = this.requests.get(m.id);
        this.requests.delete(m.id);
        if (!req) break;
        const view = decodePreview(m.bytes);
        this.painting = this.painting.then(async () => {
          if (!this.painter) this.painter = new PreviewPainter();
          const { pagesMs, drawMs } = await this.painter.paint(view.scene);
          const bitmap = await createImageBitmap(this.painter.canvas);
          const pic: Picture = { key: req.key, rules: req.rules, sky: view.sky, bitmap };
          this.cache.set(req.key, pic);
          while (this.cache.size > CACHE) {
            const [k, old] = this.cache.entries().next().value!;
            if (old === this.shown) break;
            this.cache.delete(k);
            old.bitmap.close();
          }
          if (this.want?.key === req.key) {
            this.show(pic, {
              key: req.key, theme: req.rules.theme, template: req.rules.template, seed: req.rules.seed,
              latencyMs: performance.now() - req.at, cached: false, frames: m.frames, generateMs: m.ms, pagesMs, drawMs,
            });
          }
        }).catch(e => this.error(`preview paint: ${e instanceof Error ? e.message : String(e)}`));
        break;
      }
      case 'error': this.error(m.text); break;
      default: break;
    }
  }

  private error(text: string): void {
    this.stats.errors.push(text);
    console.error(text);
  }

  private show(pic: Picture, timing: PreviewTiming): void {
    this.shown = pic;
    this.stats.shown = pic.key;
    this.stats.timings.push(timing);
    if (this.stats.timings.length > 100) this.stats.timings.shift();
    console.info(`lobby preview: ${timing.theme} ${timing.template} seed ${timing.seed}: ${timing.latencyMs.toFixed(0)} ms${timing.cached ? ' (kept)' : `, generation ${timing.generateMs?.toFixed(0)} ms in ${timing.frames} frames, pages ${timing.pagesMs?.toFixed(0)} ms, paint ${timing.drawMs?.toFixed(0)} ms`}`);
    this.setBusy(false);
    void uiSprite(pic.sky).then(b => {
      if (this.shown === pic) {
        this.sky = b;
        this.draw();
      }
    });
    this.draw();
  }

  private setBusy(busy: boolean): void {
    if (busy && !this.stats.busy) this.busySince = performance.now();
    this.stats.busy = busy;
    if (busy && !this.frame) this.frame = requestAnimationFrame(() => this.animate());
    this.draw();
  }

  private animate(): void {
    this.frame = 0;
    this.draw();
    if (this.stats.busy) this.frame = requestAnimationFrame(() => this.animate());
  }

  /** The sky, the picture unless the generator is busy, else the loading icon. */
  private draw(): void {
    const dpr = window.devicePixelRatio || 1;
    const w = Math.round(PREVIEW_CSS_W * dpr), h = Math.round(PREVIEW_CSS_H * dpr);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    const ctx = this.ctx;
    ctx.clearRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'low';
    if (this.sky) ctx.drawImage(this.sky, 0, 0, w, h);
    if (!this.stats.busy) {
      if (this.shown) ctx.drawImage(this.shown.bitmap, 0, 0, w, h);
      return;
    }
    this.xom ??= new UiXom();
    const s = 128 * dpr;
    this.xom.draw(ctx, {
      key: 'lobby-preview-loading', xdesc: LOADING_XDESC, designW: 128, designH: 128, camera: [0, 0, 300],
      clip: 'Go', time: (performance.now() - this.busySince) / 1000, loop: true, composite: 0,
    }, [w / 2 - dpr - s / 2, h / 2 + dpr - s / 2, s, s], [1, 1, 1, 1]);
  }
}
